import { useState } from "react";
import PropTypes from "prop-types";
import { AlertCircle, Cloud, Loader2, Monitor, RefreshCw, Trash2, X } from "lucide-react";
import SyncIndicator from "../SyncIndicator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { MediaDisplay, MediaPreview } from "./NoteMedia";

const renderMarkdown = text => (text || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
  .replace(/\*([^*]+)\*/g, "<em>$1</em>").replace(/_([^_]+)_/g, "<em>$1</em>");

function UploadIcon({ note, isUploading }) {
  if (note.type === "text") return <Cloud size={12} className="text-green-500" aria-label="Synced to cloud" />;
  if (isUploading) return <Loader2 size={12} className="animate-spin text-blue-500" aria-label="Uploading" />;
  if (note.upload_status === "complete") return <Cloud size={12} className="text-green-500" aria-label="In cloud" />;
  if (note.upload_status === "failed") return <AlertCircle size={12} className="text-red-500" aria-label="Upload failed" />;
  if (["pending", "uploading"].includes(note.upload_status)) return <Loader2 size={12} className="animate-spin text-blue-500" aria-label="Uploading" />;
  return <Monitor size={12} className="text-slate-400" aria-label="Device only" />;
}

export default function NoteCard({ note, userId, token, onDelete, onRetry, isUploading }) {
  const [showMedia, setShowMedia] = useState(false);
  const isOwner = note.doctor_id === userId;
  return (
    <article className="group rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
      <header className="mb-2 flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <UploadIcon note={note} isUploading={isUploading} />
          <time dateTime={note.created_at}>{new Date(note.created_at).toLocaleString()}</time>
          {note.doctor_id !== userId && <span className="rounded bg-blue-50 px-2 py-1 font-semibold text-blue-600">Dr. {note.doctorName}</span>}
          <SyncIndicator show={!note._synced && note.type === "text"} />
        </div>
        {isOwner && (
          <div className="flex gap-1">
            {note.upload_status === "failed" && <button type="button" aria-label="Retry upload" className="inline-flex min-h-11 items-center gap-1 rounded-md px-3 py-2 text-blue-600 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" onClick={() => onRetry(note)}><RefreshCw size={14} />Retry</button>}
            <button type="button" aria-label="Delete note" className="inline-flex min-h-11 items-center gap-1 rounded-md px-3 py-2 text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2" onClick={() => onDelete(note.id)}><Trash2 size={14} />Delete</button>
          </div>
        )}
      </header>
      {note.type === "text" ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" dangerouslySetInnerHTML={{ __html: renderMarkdown(note.content) }} />
      ) : (
        <button type="button" className="block text-left" onClick={() => setShowMedia(true)}>
          <span className="text-xs font-medium italic text-slate-500">Attachment: {note.type}</span>
          <span className="mt-2 flex aspect-square max-w-[150px] items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            <MediaPreview note={note} token={token} />
          </span>
        </button>
      )}
      <Dialog open={showMedia} onOpenChange={setShowMedia}>
        <DialogContent className="max-h-[90vh] w-full max-w-[95vw] overflow-hidden border-slate-800 bg-black p-0">
          <DialogHeader className="absolute right-2 top-2 z-50">
            <DialogTitle className="sr-only">Media view</DialogTitle>
            <DialogDescription className="sr-only">View note attachment</DialogDescription>
            <button type="button" aria-label="Close media" className="inline-flex min-h-11 items-center gap-1 rounded-md bg-black/50 px-3 py-2 text-white hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" onClick={() => setShowMedia(false)}><X size={20} />Close</button>
          </DialogHeader>
          <div className="flex min-h-[50vh] items-center justify-center"><MediaDisplay note={note} token={token} /></div>
        </DialogContent>
      </Dialog>
    </article>
  );
}

UploadIcon.propTypes = { note: PropTypes.object.isRequired, isUploading: PropTypes.bool.isRequired };
NoteCard.propTypes = {
  note: PropTypes.object.isRequired, userId: PropTypes.string.isRequired, token: PropTypes.string,
  onDelete: PropTypes.func.isRequired, onRetry: PropTypes.func.isRequired, isUploading: PropTypes.bool.isRequired,
};
